import React from "react";
import PropTypes from 'prop-types'; // Add this line
import { saferUseGuidelines, helpTips, classExclusions } from './guidelines';
import { transform } from "lodash";

export default class SubstanceInfoWarnings extends React.Component {
  static propTypes = { // Add this block
    info: PropTypes.shape({
      toxicity: PropTypes.arrayOf(PropTypes.string),
      addictionPotential: PropTypes.string,
      classes: PropTypes.object
    }).isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: null,
      addictionQuizIndex: 0,
      addictionQuizAnswers: [],
      showAddictionContent: false,
      showSaferUseContent: false,
      showHelpContent: false,
      expandedGuidelines: {},
      showAdditionalTips: false, // Add this line

    };

    // Bind methods
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.toggleGuideline = this.toggleGuideline.bind(this);
    this.handleAddictionAnswer = this.handleAddictionAnswer.bind(this);
    this.resetQuiz = this.resetQuiz.bind(this);
  }

  handleButtonClick(buttonType) {
    this.setState((prevState) => {
      if (buttonType === 'saferUse') {
        return {
          showSaferUseContent: !prevState.showSaferUseContent,
          showAddictionContent: false,
          showHelpContent: false,
          activeTab: prevState.showSaferUseContent ? null : 'saferUse'
        };
      } else if (buttonType === 'helpContent') {
        return {
          showHelpContent: !prevState.showHelpContent,
          showAddictionContent: false,
          showSaferUseContent: false,
          activeTab: prevState.showHelpContent ? null : 'helpContent'
        };
      }
      return {};
    });
  }

  capitalizeFirstWord(text) {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  setActiveTab(tab) {
    this.setState({ activeTab: tab });
  }

  callEmergencyNumber() {
    const lang = navigator.language || navigator.userLanguage;
    const emergencyNumbers = {
      'en-US': '911',
      'en-CA': '911',
      'en-AU': '000'
    };

    const number = emergencyNumbers[lang] || '112';
    window.location.href = `tel:${number}`;
  }

  getEmergencyButtonData() {
    const lang = navigator.language || navigator.userLanguage;
    const emergencyData = {
      'en-US': { label: 'Call 911', number: '911' },
      'en-CA': { label: 'Call 911', number: '911' },
      'en-AU': { label: 'Call 000', number: '000' }
    };

    return emergencyData[lang] || { label: 'Call 112', number: '112' };
  }

  handleAddictionAnswer(answer) {
    this.setState((prevState) => {
      const nextIndex = prevState.addictionQuizIndex + 1;
      const answers = [...prevState.addictionQuizAnswers, answer];
      return {
        addictionQuizIndex: nextIndex,
        addictionQuizAnswers: answers,
      };
    });
  }

  resetQuiz() {
    this.setState({
      addictionQuizIndex: 0,
      addictionQuizAnswers: [],
    });
  }

  calculateAddictionScore() {
    const { addictionQuizAnswers } = this.state;
    const score = addictionQuizAnswers.reduce((sum, answer) => sum + (answer ? 1 : 0), 0);

    let scoreText;
    if (score >= 6) scoreText = "Severe substance abuse.";
    else if (score >= 4) scoreText = "Moderate substance abuse.";
    else if (score >= 2) scoreText = "Mild substance abuse.";
    else scoreText = "No significant signs of substance abuse.";

    return {
      scoreText,
      notice: "No single test is completely accurate. You should always consult your physician when making decisions about your health."
    };
  }

  toggleGuideline(guidelineIndex) {
    this.setState((prevState) => ({
      expandedGuidelines: {
        ...prevState.expandedGuidelines,
        [guidelineIndex]: !prevState.expandedGuidelines[guidelineIndex],
      },
    }));
  }

  getSaferUseGuidelines(classes) {
    let guidelines = [];

    const isExcluded = (className, exclusions) => {
      const excludedClasses = exclusions[className] || [];
      return excludedClasses.some(excludedClass => 
        Object.values(classes).flat().includes(excludedClass)
      );
    };

    if (classes && Object.keys(classes).length > 0) {
      Object.entries(classes).forEach(([category, classList]) => {
        if (Array.isArray(classList) && classList.length > 0) {
          classList.forEach((className) => {
            const key = className.toLowerCase();
            
            if (saferUseGuidelines[key] && !isExcluded(key, classExclusions)) {
              guidelines = [...guidelines, ...saferUseGuidelines[key]];
            }
          });
        }
      });
    }

    if (guidelines.length === 0) {
      guidelines = saferUseGuidelines.generic || [];
    }

    return (
      <table style={{ borderCollapse: 'separate', borderSpacing: '0 10px' }}>
        <tbody>
          {guidelines.map((guideline, index) => (
            <React.Fragment key={index}>
              <tr
                style={{ marginBottom: '10px', cursor: 'pointer' }}
                onClick={() => this.toggleGuideline(index)}
              >
                <td style={{ width:'0%', padding: '5px 10px' }}>
                  <i className={`zmdi ${guideline.icon}`} style={{ fontSize: '24px', verticalAlign: 'middle' }}></i>
                </td>
                <td style={{ width:'100%', verticalAlign: 'middle' }}>
                  {guideline.text}
                </td>
              </tr>
              {/* {this.state.expandedGuidelines[index] && (
                <tr>
                  <td colSpan="2" style={{ padding: '0 10px' }}>
                      {guideline.subGuidelines && this.renderSubGuidelines(guideline.subGuidelines)}
                  </td>
                </tr>
              )} */}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    );
  }

  getHelpTips() {
    const { classes } = this.props.info;
  
    // Function to check if a tip's category should be excluded
    const isExcluded = (tipCategory) => {
      // Check if the tip's category is in the exclusions
      if (classExclusions[tipCategory]) {
        // Check if any class in `classes` is in the exclusions for this category
        return Object.values(classes).flat().some(className =>
          classExclusions[tipCategory].includes(className)
        );
      }
      return false;
    };
  
    // Split tips into default and additional based on the updated logic
    const defaultTips = helpTips.filter(tip => !isExcluded(tip.category));
    const additionalTips = helpTips.filter(tip => isExcluded(tip.category));
  
    return (
      <div>
        <table style={{ borderCollapse: 'collapse', borderSpacing: '0' }}>
          <tbody>
            {defaultTips.map((tip, index) => (
              <React.Fragment key={index}>
                <tr
                  style={{ cursor: 'pointer' }}
                  onClick={() => this.toggleGuideline(index)}
                >
                  <td style={{ width:'0%', padding: '5px 10px', textAlign: 'left' }}>
                    <i className={`zmdi ${tip.icon}`} style={{ fontSize: '24px', verticalAlign: 'middle' }}></i>
                  </td>
                  <td style={{ width:'100%', padding: '5px 10px', verticalAlign: 'middle' }}>
                    {tip.text}
                  </td>
                </tr>
                {this.state.expandedGuidelines[index] && (
                  <tr>
                    <td colSpan="2" style={{ padding: '0 10px' }}>
                      {tip.subGuidelines && this.renderSubGuidelines(tip.subGuidelines)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
        
        {additionalTips.length > 0 && (
          <div>
            <div
              style={{ cursor: 'pointer', marginTop: '20px', color: '#rgb(50, 50, 50)', textDecoration: 'underline', marginLeft: '6px' }}
              onClick={() => this.setState(prevState => ({ showAdditionalTips: !prevState.showAdditionalTips }))}
            >
              Click here for more
            </div>
            {this.state.showAdditionalTips && (
              <table style={{ borderCollapse: 'collapse', borderSpacing: '0', marginTop: '10px' }}>
                <tbody>
                  {additionalTips.map((tip, index) => (
                    <React.Fragment key={index}>
                      <tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => this.toggleGuideline(index + 100)} // Ensure index does not conflict
                      >
                        <td style={{ width:'0%', padding: '5px 10px', textAlign: 'left' }}>
                          <i className={`zmdi ${tip.icon}`} style={{ fontSize: '24px', verticalAlign: 'middle' }}></i>
                        </td>
                        <td style={{ width:'100%', padding: '5px 10px', verticalAlign: 'middle' }}>
                          {tip.text}
                        </td>
                      </tr>
                      {this.state.expandedGuidelines[index + 100] && (
                        <tr>
                          <td colSpan="2" style={{ padding: '0 10px' }}>
                            {tip.subGuidelines && this.renderSubGuidelines(tip.subGuidelines)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  }
  
  renderSubGuidelines(subGuidelines) {
    if (!subGuidelines || subGuidelines.length === 0) return null;

    return (
      <table style={{ borderCollapse: 'separate', borderSpacing: '0 5px' }}>
        <tbody>
          {subGuidelines.map((subGuideline, index) => (
            <tr key={index} style={{ marginBottom: '5px' }}>
              <td style={{ padding: '5px 10px' }}>
                <i className={`zmdi ${subGuideline.icon}`} style={{ fontSize: '20px', verticalAlign: 'middle' }}></i>
              </td>
              <td style={{ padding: '5px 10px', verticalAlign: 'middle' }}>
                {subGuideline.text}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  handleButtonClick(buttonType) {
    this.setState((prevState) => {
      if (buttonType === 'saferUse') {
        return {
          showSaferUseContent: !prevState.showSaferUseContent,
          showAddictionContent: false,
          showHelpContent: false,
          activeTab: prevState.showSaferUseContent ? null : 'saferUse'
        };
      } else if (buttonType === 'helpContent') {
        return {
          showHelpContent: !prevState.showHelpContent,
          showAddictionContent: false,
          showSaferUseContent: false,
          activeTab: prevState.showHelpContent ? null : 'helpContent'
        };
      }
      return {};
    });
  }

  render() {
    const { info } = this.props;
    const { activeTab, showSaferUseContent, showHelpContent, addictionQuizIndex } = this.state;

    let toxicityText = null;
    if (Array.isArray(info.toxicity)) {
      toxicityText = this.capitalizeFirstWord(info.toxicity.join(", "));
    }

    let addictionPotentialText = info.addictionPotential
      ? this.capitalizeFirstWord(info.addictionPotential)
      : null;

    let combinedText = [toxicityText, addictionPotentialText].filter(Boolean).join(". ");
    if (combinedText && !combinedText.endsWith(".")) {
      combinedText += ".";
    }

    combinedText = combinedText
      ? `${combinedText} The best way to avoid any substance risk is to avoid drug use.`
      : "The best way to avoid any substance risk is to avoid drug use.";

    console.log(combinedText);

    const commonButtonCardStyles = {
      outline: '0',
      border: '0',
      cursor: 'pointer',
      background: '#FCFCFD',
      boxShadow: '0px 2px 4px rgb(45 35 66 / 40%), 0px 7px 13px -3px rgb(45 35 66 / 30%), inset 0px -3px 0px #d6d6e7',
      height: '48px',
      // padding: '0 32px',
      borderRadius: '6px',
      color: '#36395a',
      transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      alignContent: 'center',
    };

    const styles = {
      combinedText: {
        fontSize: '16px',
        marginTop: '0px',
        marginLeft: '3px'
        // margin: '0px 10px 0px 10px'
      },
      card: {
        ...commonButtonCardStyles,
        textAlign: 'center',
        margin: '30px 0px',
      },
      helpMessage: {
        fontSize: '16px',
      },
      helpContent: {
        padding: '20px',
        backgroundColor: '#f9f9f9',
        border: '1px solid #ddd',
        borderRadius: '8px',
        fontSize: '14px',
        margin: '30px 0px 30px',
      },
      toggleButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '30px',
        marginBottom: '20px',
        gap: '20px'
      },
      toggleButton: {
        ...commonButtonCardStyles,
        // margin: '0 10px',
        flex: 1,
        textAlign: 'center',
      },
      activeButton: { // Add this style
        backgroundColor: '#e0e0e0',
      },
      inactiveButton: { // Add this style
        backgroundColor: '#ffffff',
      },
      saferUseText: {
        marginTop: '0px',
        fontSize: '16px',
        lineHeight: '1.5',
        marginBottom: '50px',
      },
      quizContainer: {
        textAlign: 'center',
      },
      quizQuestion: {
        marginTop: '40px',
        marginBottom: '30px',
      },
      resetButton: {
        ...commonButtonCardStyles,
        display: 'block',
        margin: '30px 10px 50px 10px',
        textAlign: 'center',
        fontWeight: '500',
      },
      emergencyButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
      },
      emergencyButton: {
        height: '50px',
        width: '100%',
        boxShadow: '0 2px 4px rgba(50, 35, 66, 0.4), 0 7px 13px -3px rgba(66, 35, 51, 0.3), 0 -3px 0 rgb(181, 13, 76) inset',
        display: 'inline-block',
        outline: 'none',
        fontSize: '16px',
        lineHeight: '20px',
        fontWeight: '600',
        borderRadius: '8px',
        padding: '14px 24px',
        border: 'none',
        transition: 'box-shadow 0.2s ease 0s, transform 0.1s ease 0s',
        background: 'linear-gradient(178deg, rgb(230, 30, 77), rgb(228, 28, 73), rgb(214, 4, 51))',
        color: 'white',
        margin: '20px 0px 10px'
      },
      emergencyButtonHover: {
        backgroundColor: '#e60000',
      },
      radioButtonContainer: {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: '20px',
        marginBottom: '30px'
      },
      radioLabel: {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
        margin: '0px 45px 30px 45px',
        cursor: 'pointer',
      },
      radioInput: {
        marginRight: '10px',
      },
    };

    const quizQuestions = [
      "Do you often find yourself thinking about substance use?",
      "Do you feel a need to use substances to cope with stress or emotional issues?",
      "Have you ever tried to cut down or stop using substances but failed?",
      "Do you continue using substances despite knowing they are causing problems in your life?",
      "Do you experience withdrawal symptoms when you try to stop using substances?",
      "Do you spend a significant amount of time obtaining, using, or recovering from the effects of substances?",
      "Has substance use affected your work, relationships, or other important areas of your life?"
    ];

    let content;
    if (activeTab === 'saferUse' && showSaferUseContent) {
      content = (
        <div>
          <div style={styles.helpContent}>
            {this.getSaferUseGuidelines(info.classes)}
          </div>
          <div style={styles.quizContainer}>
            {addictionQuizIndex < quizQuestions.length ? (
              <div>
                <div style={styles.quizQuestion}>
                  {quizQuestions[addictionQuizIndex]}
                </div>
                <div style={styles.radioButtonContainer}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name={`question-${addictionQuizIndex}`}
                      value="false"
                      checked={this.state.addictionQuizAnswers[addictionQuizIndex] === false}
                      onChange={() => this.handleAddictionAnswer(false)}
                      style={styles.radioInput}
                    />
                    No
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name={`question-${addictionQuizIndex}`}
                      value="true"
                      checked={this.state.addictionQuizAnswers[addictionQuizIndex] === true}
                      onChange={() => this.handleAddictionAnswer(true)}
                      style={styles.radioInput}
                    />
                    Yes
                  </label>
                </div>
                {addictionQuizIndex === quizQuestions.length && (
                  <div style={styles.resetButton} onClick={() => this.resetQuiz()}>
                    Start over
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={styles.quizQuestion}>
                  Your Score: {this.calculateAddictionScore().scoreText}
                </div>
                <div style={styles.quizQuestion}>
                  {this.calculateAddictionScore().notice}
                </div>
                <div style={styles.resetButton} onClick={() => this.resetQuiz()}>
                  Start over
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else if (activeTab === 'helpContent' && showHelpContent) {
      const emergencyData = this.getEmergencyButtonData();
      content = (
        <div style={styles.helpContent}>
          {this.getHelpTips()}
          <div style={styles.emergencyButtonContainer}>
            <button
              style={styles.emergencyButton}
              onClick={() => this.callEmergencyNumber()}
            >
              {emergencyData.label}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <div style={styles.combinedText}>
          {combinedText}
        </div>

        <div style={styles.toggleButtonContainer}>
          <div
            style={{
              ...styles.toggleButton,
              ...(activeTab === 'helpContent' ? styles.activeButton : styles.inactiveButton),
            }}
            onClick={() => this.handleButtonClick('helpContent')}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            onMouseDown={e => e.currentTarget.style.transform = 'translateY(2px)'}
            onMouseUp={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Emergency
          </div>
          <div
            style={{
              ...styles.toggleButton,
              ...(activeTab === 'saferUse' ? styles.activeButton : styles.inactiveButton),
            }}
            onClick={() => this.handleButtonClick('saferUse')}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            onMouseDown={e => e.currentTarget.style.transform = 'translateY(2px)'}
            onMouseUp={e => e.currentTarget.style.transform = 'translateY(0)'}
          >
            Safer use
          </div>
        </div>
        {content}
      </div>
    );
  }
}
